import React, { useCallback, useMemo } from 'react';
import SimpleBar from 'simplebar-react';

import { UpgradeDiamondIcon } from 'assets/icons/library/UpgradeDiamondIcon';
import { Toggle, ToggleState } from 'components/elements/Toggle/Toggle';
import CloseIcon from 'components/newListeningExperience/icons/CloseIcon';
import { useSkipContentSyncPause } from 'hooks/useSettingsPanelSync';
import { useTranslation } from 'hooks/useTypedTranslation';
import { AnalyticsEventKey, logAnalyticsEvent } from 'modules/analytics/logAnalyticsEvent';
import { useAuthStore } from 'modules/auth/store/authStore';
import {
  SKIP_CONTENT_SETTINGS_FREE_KEYS,
  SKIP_CONTENT_SETTINGS_KEYS,
  SKIP_CONTENT_SETTING_KEY_TO_ANALYTICS_TYPE,
  skipContentSettingNames
} from 'modules/listening/features/settings/settings';
import { showUpsellModal } from 'modules/upsells/stores/actions/showUpsellModal';
import { UpsellModalType } from 'modules/upsells/stores/types';
import { Dropdown, DropdownItem } from 'modules/user-page/components/common/Dropdown';
import { getUserSubscriptionState } from 'modules/user-page/helpers/user';

import { ChevronLeftIcon, SkipAllIcon } from '../icons';
import AutoSkipContentIcon from '../icons/AutoSkipContentIcon';
import { useAutoSkipToggleSettings } from './hooks/useAutoSkipToggleSettings';

export type AutoSkipContentPanelProps = {
  autoSkipContentUnlocked: boolean;
  autoSkipContentUnlockedReason?: unknown;
  onBack: () => void;
  onClose: () => void;
  onAllSkipSettingsChange: (checked: boolean) => void;
  onAutoSkipContentChange: (settingName: (typeof skipContentSettingNames)[number], checked: boolean) => void;
  skipContentSettings: Record<string, boolean>;
};

const AutoSkipContentPanel: React.FC<AutoSkipContentPanelProps> = ({
  autoSkipContentUnlocked,
  onBack,
  onClose,
  onAllSkipSettingsChange,
  onAutoSkipContentChange,
  skipContentSettings
}) => {
  const { t } = useTranslation('common');

  const currentUser = useAuthStore(state => state.user);
  const { isFreeUser } = getUserSubscriptionState(currentUser);

  useSkipContentSyncPause();

  const getIndividualSkipContentSettingsState = useCallback(
    (key: (typeof skipContentSettingNames)[number]): ToggleState => {
      if (!autoSkipContentUnlocked) {
        if (key === 'headers' || key === 'footers' || key === 'urls') {
          return skipContentSettings[key] ? 'checked' : 'default';
        }

        return 'locked';
      }

      return skipContentSettings[key] ? 'checked' : 'default';
    },
    [autoSkipContentUnlocked, skipContentSettings]
  );

  const isSkipAllChecked = useMemo(() => {
    const settingsKeys = autoSkipContentUnlocked ? SKIP_CONTENT_SETTINGS_KEYS : SKIP_CONTENT_SETTINGS_FREE_KEYS;

    return settingsKeys.every(key => {
      const currentState = getIndividualSkipContentSettingsState(key as (typeof skipContentSettingNames)[number]);
      return currentState === 'checked';
    });
  }, [autoSkipContentUnlocked, getIndividualSkipContentSettingsState]);

  const skipAllToggleState = isSkipAllChecked ? 'checked' : 'default';

  const onSkipAllChange = useCallback(
    (checked: boolean) => {
      onAllSkipSettingsChange(checked);
    },
    [onAllSkipSettingsChange]
  );

  const handleToggleChange = useCallback(
    (settingKey: (typeof skipContentSettingNames)[number]) => {
      const currentState = getIndividualSkipContentSettingsState(settingKey);
      const isCurrentlyChecked = currentState === 'checked';
      const newValue = !isCurrentlyChecked;

      onAutoSkipContentChange(settingKey, newValue);

      logAnalyticsEvent(AnalyticsEventKey.listeningSettingsSkipContentClicked, {
        type: SKIP_CONTENT_SETTING_KEY_TO_ANALYTICS_TYPE[settingKey],
        value: newValue ? 'on' : 'off'
      });
    },
    [getIndividualSkipContentSettingsState, onAutoSkipContentChange]
  );

  const handleToggleClick = useCallback((settingState: ToggleState) => {
    if (settingState === 'locked') {
      showUpsellModal(UpsellModalType.ContextualSkipHeadersFooters);
    }
  }, []);

  const autoSkipToggleSettings = useAutoSkipToggleSettings({
    t,
    autoSkipContentUnlocked,
    skipContentSettings
  });

  const autoSkipModeOptions: DropdownItem[] = useMemo(
    () => [
      { id: 'ai-enhanced', value: t('AI Enhanced') },
      { id: 'classic', value: t('Classic') }
    ],
    [t]
  );

  const selectedAutoSkipMode = useMemo(() => {
    return (
      autoSkipModeOptions.find(option => (skipContentSettings.enhancedSkipping ? option.id === 'ai-enhanced' : option.id === 'classic')) ||
      autoSkipModeOptions[0]
    );
  }, [autoSkipModeOptions, skipContentSettings.enhancedSkipping]);

  const handleAutoSkipModeChange = (option: DropdownItem) => {
    onAutoSkipContentChange('enhancedSkipping', option.id === 'ai-enhanced');

    logAnalyticsEvent(AnalyticsEventKey.listeningSettingsAutoSkipModeChanged, {
      autoSkipMode: option.id
    });
  };

  const Separator = () => {
    return (
      <div className="py-spl-4">
        <div className="flex">
          <div className="w-5 shrink-0" />
          <div className="w-spl-9 shrink-0" />
          <div className="h-[0.5px] bg-brdr-prim-10-100 flex-1" />
        </div>
      </div>
    );
  };

  const shouldRenderSeparator = (index: number): boolean => {
    if (isFreeUser) {
      return !(index === autoSkipToggleSettings.length - 1);
    }

    return true;
  };

  return (
    <>
      <div className="flex bg-bg-prim-w-110 justify-between relative items-center px-spl-7 py-spl-6">
        <button className="hover:opacity-75 active:opacity-50" onClick={onBack}>
          <ChevronLeftIcon className="size-5 text-icn-txt-prim" />
        </button>
        <div className="text-subheading-4 grow text-center text-icn-txt-prim">{t('Auto Skip Content')}</div>
        <button className="hover:opacity-75 active:opacity-50">
          <CloseIcon onClick={onClose} className="text-icn-txt-prim" />
        </button>
      </div>

      {isFreeUser && (
        <>
          <div
            className="flex py-3.5 px-4 items-center gap-3 self-stretch cursor-pointer"
            onClick={() => showUpsellModal(UpsellModalType.ContextualSkipHeadersFooters)}
          >
            <div className="flex items-center gap-3 flex-1">
              <UpgradeDiamondIcon />
              <div className="flex flex-col justify-center items-start gap-0.5">
                <span
                  className="text-subheading-5 bg-gradient-to-r from-[#FFA82F] to-[#FF795B] bg-clip-text"
                  style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                >
                  {t('Upgrade to Premium')}
                </span>
              </div>
            </div>
          </div>
          <Separator />
        </>
      )}

      <SimpleBar className="flex-shrink min-h-0">
        <div className="flex flex-col gap-spl-7">
          <div className="flex flex-col">
            {!isFreeUser && (
              <>
                <div className="flex items-center justify-between px-spl-7 py-spl-6">
                  <div className="flex items-center gap-spl-6">
                    <div className="size-5">
                      <SkipAllIcon />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-subheading-5 text-icn-txt-prim">{t('Skip All')}</span>
                    </div>
                  </div>
                  <Toggle
                    state={skipAllToggleState}
                    onChange={() => {
                      const newValue = skipAllToggleState !== 'checked';
                      onSkipAllChange(newValue);
                    }}
                  />
                </div>
                <Separator />
              </>
            )}

            {autoSkipToggleSettings.map((setting, index) => (
              <>
                <div key={setting.key} className="flex items-center justify-between px-spl-7 py-spl-6">
                  <div className="flex items-center gap-spl-6">
                    <div className="size-5">{setting.icon}</div>
                    <div className="flex flex-col">
                      <span className="text-subheading-5 text-icn-txt-prim">{setting.title}</span>
                      <span className="text-body-6 text-icn-txt-sec">{setting.description}</span>
                    </div>
                  </div>
                  <Toggle state={setting.state} onChange={() => handleToggleChange(setting.key)} onClick={() => handleToggleClick(setting.state)} />
                </div>
                {setting.separator && shouldRenderSeparator(index) && <Separator />}
              </>
            ))}

            {!isFreeUser && (
              <>
                <div className="flex items-center justify-between px-spl-7 py-spl-6">
                  <div className="flex items-center gap-spl-6">
                    <div className="size-5">
                      <AutoSkipContentIcon />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-subheading-5 text-icn-txt-prim">{t('Auto Skip Mode')}</span>
                    </div>
                  </div>
                  <Dropdown
                    className="text-icn-txt-prim text-subheading-6 hover:opacity-75 active:opacity-50 bg-transparent hover:bg-transparent active:bg-transparent pr-0"
                    handleSelectOption={handleAutoSkipModeChange}
                    menuOpenClassName="opacity-75"
                    openUpwards={true}
                    optionClassName="text-subheading-6"
                    options={autoSkipModeOptions}
                    selectedValue={selectedAutoSkipMode}
                  />
                </div>
                <div className="text-body-6 text-icn-txt-sec text-center px-spl-7 pb-spl-6 pt-spl-2">
                  {t('Having trouble with Auto Skip? Try switching modes.')}
                </div>
              </>
            )}
          </div>
        </div>
      </SimpleBar>
    </>
  );
};

export default AutoSkipContentPanel;
